import Message, { type MessageTypeValue } from './Message';
import Result, { type ResultValue } from './Result';
import type Check from './Check';
import type { CheckSubject } from './Check';

/**
 * A report that aggregates the result of one or more checks.
 *
 * It has a result and a list of messages.
 */
class Report {
  result: ResultValue;
  messages: Message[];

  constructor() {
    this.result = Result.NO_RESULT;
    this.messages = [];
  }

  /**
   * Add a message. (arguments are overloaded to accept either a Message object
   * or type, message, check, subject)
   *
   * @param typeOrMessage A Message object or the type of the message.
   * @param message The message string
   * @param check The check that generated the message
   * @param subject The subject of the check
   */
  addMessage(message: Message): void;
  addMessage(
    type: MessageTypeValue,
    message: string,
    check?: Check<CheckSubject>,
    subject?: CheckSubject
  ): void;
  addMessage(
    typeOrMessage: MessageTypeValue | Message,
    message?: string,
    check?: Check<CheckSubject>,
    subject?: CheckSubject
  ): void {
    let messageObject;
    if (arguments.length === 1) {
      messageObject = typeOrMessage as Message;
    } else {
      messageObject = new Message(
        typeOrMessage as MessageTypeValue,
        message as string,
        check,
        subject
      );
    }
    this.messages.push(messageObject);
    this.mergeResult(messageObject);
  }

  /**
   * Merge the current result of the report with the result in the message.
   *
   * @param message a message object
   */
  mergeResult(message: Message): void {
    this.result = Math.max(this.result, message.result) as ResultValue;
  }

  /**
   * Merge this report with another one.
   *
   * @param other The other report
   */
  mergeReport(other: Report): void {
    for (const message of other.messages) {
      this.addMessage(message);
    }
  }

  /**
   * Is the report empty?
   */
  isEmpty(): boolean {
    return this.messages.length === 0;
  }

  /**
   * Is the report successful?
   */
  get successful(): boolean {
    return (
      this.result === Result.SUCCESSFUL ||
      this.result === Result.SUCCESSFUL_WITH_WARNING ||
      this.isEmpty()
    );
  }

  /**
   * Is the report successful without warnings?
   */
  get successfulWithoutWarning(): boolean {
    return this.result === Result.SUCCESSFUL || this.isEmpty();
  }

  /**
   * Return the message string for the first message or '' if the report is
   * empty.
   */
  get firstMessage(): string {
    if (this.isEmpty()) {
      return '';
    } else {
      return this.messages[0].message;
    }
  }

  /**
   * Create a new report that is the result of merging the given reports.
   *
   * @param reports the reports to merge
   */
  static mergeReports(reports: Report[]): Report {
    return reports.reduce((acc, report) => {
      acc.mergeReport(report);
      return acc;
    }, new Report());
  }
}

export default Report;
